import React, { ReactNode, useEffect, useReducer, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce/lib";

import { getApplication } from "../../api/application";
import { calculatePremium, fetchInsuredAmount, submitDupState, updateDupState } from "../../api/dup";
import { LoadingOverlay } from "../../components/loaders/LoadingOverlay";
import { PORTALTHIRD_BASE_URL } from "../../constants/constants";
import { DupFlowContext } from "../../context/DupFlowContext";
import { useDidMount } from "../../hooks/useDidMount";
import { useQueryParams } from "../../hooks/useQueryParams";
import { DupInsuranceProductType, PaymentPeriod, PersonCoverage } from "../../interfaces/dup";
import { YesOrNo } from "../../interfaces/enum";
import { ModelState } from "../../interfaces/modelState";
import { PaymentTerm } from "../../interfaces/paymentTerm";
import {
  canCalculatePremium,
  convertDupStateToCalculationModel,
  convertDupStateToSaveDupStateRequest,
} from "./DupFlow.helpers";
import {
  AcceptApplicationData,
  DupContext,
  DupState,
  InsuredAmounts,
  PersonalDetailsModel,
  PremiumTerms,
  UpdateInsuredAmountPayload,
} from "./DupFlow.interfaces";
import { dupFlowStateReducer, initialState } from "./DupFlowState";

interface Props {
  children: ReactNode;
}

export const DupFlowProvider = ({ children }: Props) => {
  const history = useHistory();
  const queryParams = useQueryParams();
  const applicationIdRef = useRef(queryParams.get("applicationId"));

  const [isLoadingExistingApplication, setIsLoadingExistingApplication] = useState(false);
  const [isLoadingPremium, setLoadingPremium] = useState(false);
  const [premium, setPremium] = useState<PremiumTerms | null>(null);
  const [modelState, setModelState] = useState<ModelState | null>(null);
  const [insuredAmounts, setInsuredAmounts] = useState<InsuredAmounts>({ minAmount: 10, maxAmount: 25000 });
  const oneTimePolicyCostsRef = useRef("");
  const [data, dispatch] = useReducer(dupFlowStateReducer, initialState);
  const [iban, setIban] = useState<string | undefined>(undefined);
  const [acceptApplication, setAcceptApplication] = useState<AcceptApplicationData | undefined>(undefined);
  const currentStepRef = useRef(1);
  const shouldRetrievePremiumsRef = useRef(false);

  const retrievePremium = useDebouncedCallback(() => {
    if (canCalculatePremium(data)) {
      setLoadingPremium(true);

      const request = {
        ...convertDupStateToCalculationModel(data),
      };

      calculatePremium(request)
        .then((response) => {
          if ("modelState" in response) {
            updateModelState(response.modelState);
          } else {
            if (
              response.totalPremiumPerTerms.length !== premium?.totalPremiumPerTerms.length ||
              premium?.totalPremiumPerTerms?.length === undefined
            ) {
              dispatch({
                type: "UPDATE_PAYMENT_TERM",
                payload: { paymentTerm: response.totalPremiumPerTerms[0].paymentTerm },
              });
            }
            setPremium(response);
            setModelState(null);
          }
        })
        .catch((e) => console.error(`Failed to load premiums information: ${e}`))
        .finally(() => {
          setLoadingPremium(false);
          shouldRetrievePremiumsRef.current = false;
        });
    }
  }, 500);

  const updateInsuredData = (index: number, insuredData: Partial<PersonalDetailsModel>) => {
    dispatch({ type: "UPDATE_INSURED_DATA", payload: { index, data: insuredData } });
  };

  const updateInsuredDateOfBirth = (index: number, dateOfBirth: string) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_DATA", payload: { index, data: { dateOfBirth } } });
  };

  const addInsuredPerson = (isChild: boolean) => {
    dispatch({ type: "CREATE_INSURED_PERSON", payload: { isChild } });
  };

  const removePerson = (index: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "DELETE_INSURED_PERSON", payload: { index } });
  };

  const updateChildAsAdultPremium = (index: number, childAsAdultPremium: YesOrNo) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_DATA", payload: { index, data: { childAsAdultPremium } } });
  };

  const updateInsuranceType = (insuranceType: DupInsuranceProductType) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURANCE_TYPE", payload: { insuranceType } });
  };

  const updateInsuredAmount = ({ insuredAmount, hasInsuredAmount }: UpdateInsuredAmountPayload) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_AMOUNT", payload: { insuredAmount, hasInsuredAmount } });
  };

  const updateHasInsuredAmount = () => {
    dispatch({ type: "UPDATE_HAS_INSURED_AMOUNT" });
  };

  const updatePaymentPeriod = (paymentPeriod: PaymentPeriod) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_PAYMENT_PERIOD", payload: { paymentPeriod } });
  };

  const updateInsuredAmounts = () => {
    if (!data.defaultCoverage.effectiveDate) {
      return;
    }

    fetchInsuredAmount(data.defaultCoverage.effectiveDate)
      .then((result) => {
        if ("minFreeAmount" in result && "maxFreeAmount" in result) {
          setInsuredAmounts({ minAmount: result.minFreeAmount, maxAmount: result.maxFreeAmount });
        }
      })
      .catch((e) => console.error(`Failed to fetch insured amounts: ${e}`));
  };

  const updateInsuredCoverage = (index: number, coverage: PersonCoverage, isApplied: boolean) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_PERSON_COVERAGE", payload: { index, coverage, isApplied } });
  };

  const updateInsuredCoverageInsuranceType = (index: number, insuranceType: DupInsuranceProductType) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_PERSON_COVERAGE_INSURANCE_TYPE", payload: { index, insuranceType } });
  };

  const updateInsuredCoverageInsuredAmount = (index: number, insuredAmount: number) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_INSURED_PERSON_COVERAGE_INSURED_AMOUNT", payload: { index, insuredAmount } });
  };

  const updateEffectiveDate = (date?: string) => {
    shouldRetrievePremiumsRef.current = true;
    dispatch({ type: "UPDATE_EFFECTIVE_DATE", payload: { date } });
    updateInsuredAmounts();
  };

  const updatePaymentTerm = (paymentTerm: PaymentTerm) => {
    dispatch({ type: "UPDATE_PAYMENT_TERM", payload: { paymentTerm } });
  };

  const updateIbanNumber = (value: string) => {
    setIban(value);
  };

  const updateAcceptCheckbox = (offerIsAccepted: boolean, agreementText: string) => {
    setAcceptApplication({ offerIsAccepted, agreementText });
  };
  const updateModelState = (updatedModelState: ModelState) => {
    setModelState({ ...updatedModelState });
  };

  const saveDupState = async () => {
    const requestData = convertDupStateToSaveDupStateRequest(
      data,
      premium,
      oneTimePolicyCostsRef.current,
      applicationIdRef.current
    );

    const response = await updateDupState(requestData);

    if (!response.applicationId) {
      throw Error("NO_APPLICATION_ID_RETURNED_FROM_SAVE_DUP_STATE");
    }

    if (response.applicationId !== applicationIdRef.current) {
      applicationIdRef.current = response.applicationId;
    }
  };

  function navigateToPath(path: string) {
    const url = new URL(window.location.href);
    const search = new URLSearchParams(url.searchParams);

    if (applicationIdRef.current) {
      search.set("applicationId", applicationIdRef.current);
    } else {
      search.delete("applicationId");
    }

    window.scrollTo(0, 0);
    history.push({
      pathname: `/${PORTALTHIRD_BASE_URL}/${path}`,
      search: search.toString(),
    });
  }

  const submitStep = async (requestData: object, url: string) => {
    const result = await submitDupState(url, {
      ...requestData,
      applicationId: applicationIdRef.current,
    });
    if (result?.modelState) {
      setModelState(result.modelState);
      throw new Error("SUBMIT_DUP_STATE_VALIDATION_FAILED");
    }
  };

  const goToStepTwo = async (path: string, policyCosts: string) => {
    oneTimePolicyCostsRef.current = policyCosts;

    await saveDupState();

    const calculationStep = convertDupStateToCalculationModel(data);

    await submitStep(calculationStep, "dupflow/submitCalculate");

    currentStepRef.current = 2;

    navigateToPath(path);
  };

  const goToStepThree = async (path: string) => {
    await saveDupState();

    await submitStep({ insuredPersons: data.insuredPersons }, "dupflow/submitPersonDetails");

    currentStepRef.current = 3;

    navigateToPath(path);
  };

  const goToStepFour = (path: string) => {
    currentStepRef.current = 4;

    navigateToPath(path);
  };

  const submitOffer = async (url: string) => {
    const totalPremiumPerTerms = { premium: { terms: premium?.totalPremiumPerTerms } };
    const request = {
      applicationId: applicationIdRef.current,
      dupApplication: { ...data, ...totalPremiumPerTerms, iban, ...acceptApplication },
    };

    await submitStep(request, "dupflow/submitOffer");

    // remove the query parameters
    window.history.replaceState(null, "", window.location.pathname);
    history.push(url);
  };

  const goBack = (stepNumber: number, path: string) => {
    currentStepRef.current = stepNumber;
    navigateToPath(path);
  };

  const retrieveApplication = async () => {
    if (!applicationIdRef.current) {
      return;
    }
    setIsLoadingExistingApplication(true);
    try {
      const insuranceData = await getApplication(applicationIdRef.current);
      const parsedInsuranceData = JSON.parse(insuranceData.json) as DupState;

      if (parsedInsuranceData.defaultCoverage.insuredAmount) {
        parsedInsuranceData.defaultCoverage.hasInsuredAmount = true;
      }

      shouldRetrievePremiumsRef.current = true;

      dispatch({ type: "REHYDRATE", payload: { data: parsedInsuranceData } });
    } catch (e) {
      console.error(`Failed to retrieve application: ${e}`);
    } finally {
      setIsLoadingExistingApplication(false);
    }
  };

  const context: DupContext = {
    acceptApplication,
    currentStep: currentStepRef.current,
    data,
    goBack,
    goToStepFour,
    goToStepThree,
    goToStepTwo,
    iban,
    insuredAmounts,
    isLoadingPremium,
    modelState,
    premium,
    setModelState: updateModelState,
    submitOffer,
    setters: {
      addInsuredPerson,
      removePerson,
      updateAcceptCheckbox,
      updateChildAsAdultPremium,
      updateEffectiveDate,
      updateHasInsuredAmount,
      updateIbanNumber,
      updateInsuranceType,
      updateInsuredAmount,
      updateInsuredCoverage,
      updateInsuredCoverageInsuranceType,
      updateInsuredCoverageInsuredAmount,
      updateInsuredData,
      updateInsuredDateOfBirth,
      updatePaymentPeriod,
      updatePaymentTerm,
    },
  };

  useDidMount(async () => {
    if (applicationIdRef.current) {
      await retrieveApplication();
    }
    updateInsuredAmounts();
  });

  useEffect(() => {
    if (shouldRetrievePremiumsRef.current) {
      retrievePremium();
    }
  }, [data, retrievePremium]);

  return (
    <DupFlowContext.Provider value={context}>
      <LoadingOverlay loading={isLoadingExistingApplication}>{children}</LoadingOverlay>
    </DupFlowContext.Provider>
  );
};
